<script setup lang="ts">
import { pb } from "@/pocketbase";
import Title from "@/components/Title.vue";
import { ContactRole, ProductTechnology, Technology, TechnologyType } from "@/model";
import { useRoute } from "vue-router";
import Heading from "@/components/Heading.vue";
import { computed, Ref } from "vue";
import Divider from "@/components/Divider.vue";
import Content from "@/components/Content.vue";

const route = useRoute();

const technology: Technology = await pb.collection('technologies').getOne<Technology>(route.params.id);
const productTechnologies: ProductTechnology[] = await pb.collection('product_technologies').getFullList<ProductTechnology>(null, {
  expand: 'product,technology,product.businessUnit,product.contacts',
  sort: 'product.name',
  filter: `technology = '${technology.id}'`,
});

type Contact = {
  name: string
  email: string
  productRoles: {
    product: string
    roles: ContactRole[]
  }[],
}

const contacts = computed(() => {
  const map = new Map<string, Contact>()
  for (const productTechnology of productTechnologies) {
    for (const contact of productTechnology.expand?.product?.expand?.contacts || []) {
      if (!map.has(contact.email)) {
        map.set(contact.email, {
          name: contact.name,
          email: contact.email,
          productRoles: [],
        });
      }
      map.get(contact.email)!.productRoles.push({
        product: productTechnology.expand!.product!.name,
        roles: contact.roles,
      });
    }
  }

  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
});

const imageUrl: Ref<string | null> = computed(() => {
  if (technology.simpleicon) {
    return `https://cdn.simpleicons.org/${technology.simpleicon}`;
  }

  return technology.logo.length > 0 ? pb.files.getUrl(technology, technology.logo) : null
});
</script>

<template>
  <Title :subtitle="`Technologies > ${TechnologyType[technology.type]}`">{{ technology.name }}</Title>
  <Divider/>
  <Content>
    <div class="flex">
      <div v-if="imageUrl" class="mr-6 pt-3">
        <img :src="imageUrl" :alt="`Logo of ${technology.name}`" class="w-24 h-24 object-contain"/>
      </div>
      <div class="flex-grow">
        <Heading>Description</Heading>
        <template v-if="technology.description.length > 0">{{ technology.description }}</template>
        <p v-else class="italic">No description available</p>

        <Heading>Website</Heading>
        <template v-if="technology.website.length > 0"><a :href="technology.website" class="default-anchor" target="_blank">{{ technology.website }}</a></template>
        <p v-else class="italic">No website available</p>
      </div>
    </div>

    <Heading>Products that use this technology</Heading>

    <p v-if="productTechnologies.length === 0">
      No products seem to be using this technology. You're on your own, sorry buddy.
    </p>

    <table v-if="productTechnologies.length > 0">
      <thead>
      <tr>
        <th>Business Unit</th>
        <th>Product</th>
        <th class="text-right">Version</th>
        <th>Comment</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="productTechnology in productTechnologies">
        <td>{{ productTechnology.expand!.product!.expand!.businessUnit!.name }}</td>
        <td>{{ productTechnology.expand!.product!.name }}</td>
        <td class="text-right">{{ productTechnology.version || 'n/a' }}</td>
        <td>{{ productTechnology.comment || 'n/a' }}</td>
      </tr>
      </tbody>
    </table>

    <template v-if="productTechnologies.length > 0">
      <Heading>People who can help you</Heading>

      <p v-if="contacts.length === 0">
        None of the products have any contacts defined. Maybe try reaching out in a group chat?
      </p>

      <table v-if="contacts.length > 0">
        <thead>
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th>Role</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="contact in contacts">
          <td>{{ contact.name }}</td>
          <td><a class="default-anchor" :href="`mailto:${contact.email}?subject=${encodeURIComponent('Question about ' + technology.name)}`">{{ contact.email }}</a></td>
          <td>
            <div v-for="productRole in contact.productRoles">
              <template v-for="(role, idx) in productRole.roles">
                <template v-if="idx > 0"> and </template>
                <span class="opacity-80">{{ ContactRole[role] }}</span>
              </template>
              for <span class="opacity-80">{{ productRole.product }}</span>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </template>
  </Content>
</template>

<style scoped lang="scss">
table {
  @apply table-auto w-full;
}

th, td {
  @apply border-y border-gray-700 p-1 text-left;
}
</style>

